import React, { useState } from 'react';
import { useCurrentUser } from '../common/withUser';
import classNames from 'classnames';
import { Link } from '@/lib/reactRouterWrapper';
import SunshineListTitle from "./SunshineListTitle";
import SunshineCuratedSuggestionsItem from "./SunshineCuratedSuggestionsItem";
import MetaInfo from "../common/MetaInfo";
import FormatDate from "../common/FormatDate";
import LoadMore from "../common/LoadMore";
import LWTooltip from "../common/LWTooltip";
import ForumIcon from "../common/ForumIcon";
import { useQuery } from "@/lib/crud/useQuery";
import { useQueryWithLoadMore } from "@/components/hooks/useQueryWithLoadMore";
import { gql } from "@/lib/generated/gql-codegen";
import { defineStyles } from '@/components/hooks/defineStyles';
import { useStyles } from '@/components/hooks/useStyles';

const PostsListMultiQuery = gql(`
  query multiPostsListQuery($selector: PostSelector, $limit: Int, $enableTotal: Boolean) {
    posts(selector: $selector, limit: $limit, enableTotal: $enableTotal) {
      results {
        ...PostsList
      }
      totalCount
    }
  }
`);

const SunshineCurationPostsListMultiQuery = gql(`
  query multiSunshineCurationPostsListQuery($selector: PostSelector, $limit: Int, $enableTotal: Boolean) {
    posts(selector: $selector, limit: $limit, enableTotal: $enableTotal) {
      results {
        ...SunshineCurationPostsList
      }
      totalCount
    }
  }
`);

const styles = defineStyles('SunshineCuratedSuggestionsList', (theme: ThemeType) => ({
  loadMorePadding: {
    paddingLeft: 16,
  },
  audioIcon: {
    width: 14,
    height: 14,
    color: theme.palette.grey[500],
    cursor: "pointer",
    '&:hover': {
      opacity: 0.5,
    }
  },
  audioOnly: {
    color: theme.palette.primary.main,
  },
  // Styling variations
  warning: {
    backgroundColor: `${theme.palette.error.main}10`,
    border: `3px solid ${theme.palette.error.main}`,
  },
  alert: {
    backgroundColor: `${theme.palette.error.main}15`,
    border: `4px solid ${theme.palette.error.main}`,
  },
  urgent: {
    backgroundColor: `${theme.palette.error.main}30`,
    border: `10px solid ${theme.palette.error.main}`,
  },
}));

const shouldShow = (atBottom: boolean, timeForCuration: boolean, currentUser: UsersCurrent | null, hasCurationDrafts: boolean) => {
  return (atBottom === hasCurationDrafts) || timeForCuration;
}

const hasCurationDrafts = (results: SunshineCurationPostsList[] | undefined): boolean => {
  if (!results || results.length === 0) return false;
  
  return results.some(post => post.curationNotices && post.curationNotices.length > 0);
}

const SunshineCuratedSuggestionsList = ({limit = 7, atBottom, setCurationPost, setHasDrafts}: {
  limit?: number,
  atBottom?: boolean,
  setCurationPost?: (post: PostsList) => void,
  setHasDrafts?: (hasDrafts: boolean) => void,
}) => {
  const classes = useStyles(styles);
  const currentUser = useCurrentUser();

  const [audioOnly, setAudioOnly] = useState<boolean>(false)

  const { data, loadMoreProps } = useQueryWithLoadMore(SunshineCurationPostsListMultiQuery, {
    variables: {
      selector: { sunshineCuratedSuggestions: { audioOnly } },
      limit,
      enableTotal: true,
    },
    itemsPerPage: 60,
  });

  const results = data?.posts?.results.filter(post => !post.reviewForCuratedUserId);

  const showLoadMore = !loadMoreProps.hidden;

  const { data: dataPostsList } = useQuery(PostsListMultiQuery, {
    variables: {
      selector: { curated: {} },
      limit: 1,
      enableTotal: false,
    },
    notifyOnNetworkStatusChange: true,
  });

  const curatedResults = dataPostsList?.posts?.results;
  const curatedDate = curatedResults ? new Date(curatedResults[0]?.curatedDate ?? 0) : new Date();
  const twoAndAHalfDaysAgo = new Date(new Date().getTime()-(2.5*24*60*60*1000));
  const timeForCuration = curatedDate <= twoAndAHalfDaysAgo;

  const hasDrafts = hasCurationDrafts(results);
  
  if (setHasDrafts) {
    setHasDrafts(hasDrafts);
  }

  if (currentUser?.hideSunshineSidebar) {
    return null
  }

  if (!shouldShow(!!atBottom, timeForCuration, currentUser, hasDrafts)) {
    return null
  }

  let statusClass = '';
  const daysSinceCurated = Math.floor(
    (new Date().getTime() - curatedDate.getTime()) / (24 * 60 * 60 * 1000)
  );
  if (daysSinceCurated >= 6) {
    statusClass = classes.urgent;
  } else if (daysSinceCurated >= 4) {
    statusClass = classes.alert;
  } else if (daysSinceCurated >= 3) {
    statusClass = classes.warning;
  }

  const needsDraftsText = !timeForCuration && !hasDrafts ? " (No drafts!)" : "";
  return (
    <div className={statusClass}>
      <SunshineListTitle>
        <Link to={`/admin/curation`}>Suggestions for Curated{needsDraftsText}</Link>
        <MetaInfo>
          <FormatDate date={curatedDate}/>
        </MetaInfo>
        <LWTooltip title="Filter to only show audio">
          <ForumIcon
            icon="VolumeUp"
            className={classNames(classes.audioIcon, {[classes.audioOnly]: audioOnly})}
            onClick={() => setAudioOnly(!audioOnly)}
          />
        </LWTooltip>
      </SunshineListTitle>
      {results?.map(post =>
        <div key={post._id} >
          <SunshineCuratedSuggestionsItem post={post} setCurationPost={setCurationPost}/>
        </div>
      )}
      {showLoadMore && <div className={classes.loadMorePadding}>
        <LoadMore {...loadMoreProps}/>
      </div>}
    </div>
  )
}

export default SunshineCuratedSuggestionsList;


